import React, { useEffect, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';

import { Table } from '@qality/common-components/src/atoms/Table';
import { useTestCaseFilterContext } from '@qality/common-components/src/test-cycle/TestCaseFilter';
import { FEATURE_FLAG } from '@qality/common-config';
import { TestCaseInCycle, User } from '@qality/common-model';
import {
  DragDropService,
  TestCycleService,
  UserService,
} from '@qality/common-service';

import { useTestCycleSorterContext } from '../../TestCycleSorter';
import { useTestCasesInCycleList } from '../TestCaseInCycleListProvider';
import { TestCasesInCycleTableRow } from './TestCaseInCycleTableRow';
import { TestCasesInCycleTableRowEmpty } from './TestCaseInCycleTableRowEmpty';

type Props = {
  selectedTestCasesIds: Array<number>;
  onTestCaseSelection: (testCase: TestCaseInCycle) => void;
};

export function TestCasesInCycleTableBody({
  selectedTestCasesIds,
  onTestCaseSelection,
}: Props): JSX.Element {
  const [defaultUserPickerOptions, setDefaultUserPickerOptions] =
    useState<Promise<Array<User>>>();

  const { testCycleActions, testCycle } = useTestCasesInCycleList();
  const { filterTestCaseFunction, isTestCaseFilterActive } =
    useTestCaseFilterContext();

  useEffect(() => {
    fetchDefaultUserPickerOptions();
  }, []);

  const fetchDefaultUserPickerOptions = () => {
    setDefaultUserPickerOptions(
      UserService.getAssignableToProjectUserOptions(testCycle?.project.key)
    );
  };

  const { testCycleSortFunction, isTestCycleSorted } =
    useTestCycleSorterContext();

  const onDragEnd = (result) => {
    DragDropService.handleDragEnd(testCycle.testCases, result);

    const testCasesWithUpdatedPosition = testCycle.testCases.map(
      (testCase, index) => ({
        ...testCase,
        position: index,
      })
    );

    TestCycleService.updateTestCasesInCyclePositions(
      testCasesWithUpdatedPosition,
      testCycle.id
    );

    const clonedTestCycle = cloneDeep(testCycle);
    clonedTestCycle.testCases = testCasesWithUpdatedPosition;

    testCycleActions.updateTestCycle(clonedTestCycle);
  };

  const isDndDisabled =
    !FEATURE_FLAG.isPremium ||
    !testCycle.canEdit ||
    isTestCycleSorted ||
    isTestCaseFilterActive;

  const currentTestCases = [...testCycle.testCases]
    .filter(filterTestCaseFunction)
    .sort(testCycleSortFunction);

  const isCurrentTestCasesEmpty = currentTestCases.length === 0;

  if (isCurrentTestCasesEmpty) {
    return (
      <Table.Body>
        <TestCasesInCycleTableRowEmpty />
      </Table.Body>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId='droppableId' isDropDisabled={isDndDisabled}>
        {(provided) => {
          return (
            <Table.Body
              innerRef={provided.innerRef}
              {...provided.droppableProps}
            >
              {currentTestCases.map((testCaseInCycle, index) => (
                <TestCasesInCycleTableRow
                  key={testCaseInCycle.id}
                  testCaseInCycle={testCaseInCycle}
                  index={index}
                  isSelected={selectedTestCasesIds.includes(testCaseInCycle.id)}
                  onTestCaseSelection={onTestCaseSelection}
                  defaultUserPickerOptions={defaultUserPickerOptions}
                />
              ))}
              {provided.placeholder}
            </Table.Body>
          );
        }}
      </Droppable>
    </DragDropContext>
  );
}
